import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {UserIcon} from "@heroicons/react/outline";
import {PlusIcon, UserIcon as UserIconSolid} from "@heroicons/react/solid";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {User} from "types/User";
import {useNavigate} from "react-router-dom";
import {useAuth} from "context/AuthContext";
import DeleteUser from "pages/User/Delete";
import SearchBar from "components/SearchBar";
import IconBadge from "components/IconBadge";
import IconName from "types/IconName";
import NotAuthorised from "pages/NotAuthorised";
import UserContextMenu from "components/menus/UserContextMenu";
import EmailVerificationStatus from "../../../components/EmailVerificationStatus";
import BooleanIndicator from "../../../components/BooleanIndicator";

// Component ----

function UserList() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const {data: roles, isLoading: isLoadingRoles} = useQuery(['roles'], () => ngage.roles.all());

  const audienceRoleId = roles?.find(role => role.name === 'audience')?.id;

  const {data: users, isFetching, isLoading: isLoadingUsers} = useQuery(
    ['users.operator', {search, page, roles: `!${String(audienceRoleId)}`}],
    () => ngage.users.filtered({search, roles: `!${String(audienceRoleId)}`, page}),
    {keepPreviousData: true, enabled: auth.can('users:read') && !!audienceRoleId}
  )

  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  // Prevent unauthorised viewing
  if (auth.cannot('users:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetch('users.operator', {page, search, roles: '!4'}, ngage.users.filtered)

  // Prepare meta data
  const meta = [
    {icon: UserIconSolid, value: users?.meta.total ? users?.meta.total.toLocaleString() + ' users' : ''}
  ]

  // Prepare breadcrumbs
  const breadcrumbs = {'Admin': '/', 'Users': '/users'}

  // Prepare page actions
  const actions = (
    <>
      {(auth.can('users:write') && !auth.roles.includes('audience manager')) &&
        <Button icon={PlusIcon} onClick={() => navigate('/users/new')}>New</Button>
      }
    </>
  )

  return (
    <Page
      title="Operator Users"
      meta={meta}
      breadcrumbs={breadcrumbs}
      actions={actions}
    >

      {/* Search and filtering */}
      <div className="mb-4 w-full grid md:grid-cols-4">
        <SearchBar isSearching={isFetching} onChange={setSearch}/>
      </div>

      <Table paginationData={users} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Email</TableHeader>
            <TableHeader>Role</TableHeader>
            <TableHeader>2FA</TableHeader>
            <TableHeader
              alignment={TableHeader.alignments.CENTER}
            >
              App Permissions
            </TableHeader>
            <TableHeader>Registered</TableHeader>
            <TableHeader>Last Login</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoadingRoles || isLoadingUsers}>

          {users && users.data.length < 1 &&
            <TableRow index={0}>
              <TableData colSpan={6}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <UserIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No users</span>
                </div>
              </TableData>
            </TableRow>
          }

          {users?.data.map((user, index) => (
            <TableRow key={user.id} index={index} highlight>
              <TableData>
                <div className="flex">
                  <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                    <UserContextMenu
                      user={user}
                      onDelete={() => setDeletingUser(user)}
                      position="left"
                    />
                  </div>

                  <div className="flex group-hover:translate-x-1 transition-all">
                    <UserIcon className="h-5 w-5 mr-2 text-gray-500"/> {user.name}
                  </div>
                </div>
              </TableData>
              <TableData>
                <EmailVerificationStatus email={user.email} isVerified={user.email_is_verified} />
              </TableData>
              <TableData transform={TableData.transforms.CAPITALIZE}>
                <IconBadge
                  text={user.role_names[0] ?? 'Unknown'}
                  icon={IconName.USER_CIRCLE}
                  color={IconBadge.colors.BRAND}
                />
              </TableData>
              <TableData alignment={TableData.alignments.CENTER}>
                <BooleanIndicator value={user.two_factor_confirmed}/>
              </TableData>
              <TableData alignment={TableData.alignments.CENTER}>
                <BooleanIndicator value={!!user.app_permissions}/>
              </TableData>
              <TableData>{formattedDateTime(user.created_at) || 'Never'}</TableData>
              <TableData>{formattedDateTime(user.last_logged_in_at) || 'Never'}</TableData>
              <TableData alignment={TableData.alignments.RIGHT}>
                <UserContextMenu user={user} onDelete={() => setDeletingUser(user)}/>
              </TableData>

              {/* Delete confirmation modal */}
              <DeleteUser
                user={deletingUser}
                show={user === deletingUser}
                onCancel={() => setDeletingUser(null)}
                onDelete={() => {
                  setDeletingUser(null)
                  navigate('/users')
                }}
                onDeleteFail={() => setDeletingUser(null)}
              />
            </TableRow>
          ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default UserList;